import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

interface BallProps {
  frontColor: string;
  backColor: string;
  size: number;
  sizeUnit: string;
  x: number;
  y: number;
  index: number;
}

const impulse = (props: BallProps) => keyframes`
  0% {
    background: ${props.backColor};
    transform: scale(1);
    animation-timing-function: cubic-bezier(1, 0, 0.7, 1);
  }
  40% {
    background: ${props.frontColor};
    transform: scale(1.5);
    animation-timing-function: cubic-bezier(0.3, 0, 0, 1);
  }
  72.5% {
    background: ${props.backColor};
    transform: scale(1);
    animation-timing-function: linear;
  }
  100% {
    background: ${props.backColor};
    transform: scale(1);
  }
`;

const Wrapper = styled.div<{ size: number; sizeUnit: string }>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${(props) => `${props.size}${props.sizeUnit}`};
  height: ${(props) => `${props.size / 5}${props.sizeUnit}`};
`;

const Ball = styled.div<BallProps>`
  position: absolute;
  top: ${(props) => `${props.y}${props.sizeUnit}`};
  left: ${(props) => `${props.x}${props.sizeUnit}`};
  width: ${(props) => `${props.size / 5}${props.sizeUnit}`};
  height: ${(props) => `${props.size / 5}${props.sizeUnit}`};
  border-radius: 50%;
  background-color: ${(props) => props.frontColor};
  animation: ${(props) => impulse(props)} 1.25s linear infinite;
  animation-delay: ${(props) => props.index * 0.125}s;
`;

interface ImpulseSpinnerProps {
  size?: number;
  frontColor?: string;
  backColor?: string;
  loading?: boolean;
  sizeUnit?: string;
}

const COUNT_BALLS = 3;

/** Drop-in replacement for react-spinners-kit's ImpulseSpinner: renders pulsing balls that scale/color-cycle. */
export const ImpulseSpinner = ({
  size = 40,
  frontColor = '#00ff89',
  backColor = '#4b4c56',
  loading = true,
  sizeUnit = 'px',
}: ImpulseSpinnerProps) => {
  if (!loading) return null;

  return (
    <Wrapper size={size} sizeUnit={sizeUnit}>
      {Array.from({ length: COUNT_BALLS }, (_, i) => (
        <Ball
          frontColor={frontColor}
          backColor={backColor}
          size={size}
          x={i * (size / 5 + size / 5)}
          y={0}
          key={i}
          index={i}
          sizeUnit={sizeUnit}
        />
      ))}
    </Wrapper>
  );
};

export default ImpulseSpinner;
